import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

const CaseStudies = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  // DESKTOP ONLY animations
  const card1Opacity = useTransform(scrollYProgress, [0.1, 0.25], [0, 1]);
  const card1Y = useTransform(scrollYProgress, [0.1, 0.25], [60, 0]);
  
  const card2Opacity = useTransform(scrollYProgress, [0.2, 0.35], [0, 1]);
  const card2Y = useTransform(scrollYProgress, [0.2, 0.35], [60, 0]);
  
  const card3Opacity = useTransform(scrollYProgress, [0.3, 0.45], [0, 1]);
  const card3Y = useTransform(scrollYProgress, [0.3, 0.45], [60, 0]);

  const cases = [
    {
      company: 'B2B SaaS Platform',
      problem: 'Unclear positioning in crowded market',
      solution: 'Repositioned around unique bottleneck removal capability',
      result: '3.2x increase in qualified pipeline',
      metric: '3.2x',
      metricLabel: 'Pipeline Growth',
      icon: '🎯',
      delay: 0,
      opacity: card1Opacity,
      y: card1Y,
    },
    {
      company: 'Enterprise Software',
      problem: 'Complex product story confusing buyers',
      solution: 'Simplified narrative around core value driver',
      result: '47% reduction in sales cycle length',
      metric: '-47%',
      metricLabel: 'Sales Cycle',
      icon: '⚡',
      delay: 0.15,
      opacity: card2Opacity,
      y: card2Y,
    },
    {
      company: 'Growth-Stage Startup',
      problem: 'Weak market narrative limiting fundraising',
      solution: 'Rebuilt story around market transformation',
      result: 'Closed $18M Series A at 2x target valuation',
      metric: '2x',
      metricLabel: 'Valuation',
      icon: '🚀',
      delay: 0.3,
      opacity: card3Opacity,
      y: card3Y,
    },
  ];

  return (
    <section ref={sectionRef} className="relative py-32 lg:py-40 overflow-hidden bg-[#080808]">
      {/* Ambient glow - desktop only */}
      <div className="hidden lg:block absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full bg-accent-blue blur-[200px] opacity-12" />
      </div>

      {/* Minimal static glow for mobile */}
      <div className="lg:hidden absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[250px] h-[250px] rounded-full bg-accent-blue blur-[80px] opacity-8" />
      </div>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 lg:mb-20"
        >
          <h2 className="text-[32px] lg:text-6xl font-bold text-white mb-4 lg:mb-6 leading-tight">
            Results
          </h2>
          <p className="text-[15px] lg:text-xl text-gray-400 max-w-[700px] mx-auto leading-relaxed">
            What happens when you identify and remove the right bottleneck
          </p>
        </motion.div>

        {/* DESKTOP VERSION - Animated triangular layout */}
        <div className="hidden lg:block max-w-[1100px] mx-auto">
          {/* Top row - 2 cards */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            {cases.slice(0, 2).map((caseStudy, index) => (
              <motion.div
                key={caseStudy.company}
                style={{ opacity: caseStudy.opacity, y: caseStudy.y }}
                className="group"
              >
                <div className="bg-gradient-to-br from-[#0f1419] to-[#0a0e13] border border-white/10 rounded-2xl p-8 h-full relative overflow-hidden hover:border-accent-blue/40 transition-all duration-500">
                  <div className="absolute inset-0 bg-gradient-to-br from-accent-blue/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="relative z-10">
                    <div className="flex items-start justify-between mb-6">
                      <div className="text-4xl">{caseStudy.icon}</div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-accent-blue mb-1">{caseStudy.metric}</div>
                        <div className="text-xs text-gray-500 font-semibold tracking-wider uppercase">{caseStudy.metricLabel}</div>
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-white mb-4">{caseStudy.company}</h3>

                    <div className="space-y-4">
                      <div>
                        <div className="text-xs text-red-400 font-semibold tracking-wider uppercase mb-2">Problem</div>
                        <p className="text-sm text-gray-400 leading-relaxed">{caseStudy.problem}</p>
                      </div>

                      <div>
                        <div className="text-xs text-accent-blue font-semibold tracking-wider uppercase mb-2">Solution</div>
                        <p className="text-sm text-gray-300 leading-relaxed">{caseStudy.solution}</p>
                      </div>

                      <div>
                        <div className="text-xs text-green-400 font-semibold tracking-wider uppercase mb-2">Result</div>
                        <p className="text-sm text-white font-medium leading-relaxed">{caseStudy.result}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bottom row - 1 centered card */}
          <div className="flex justify-center">
            <motion.div
              style={{ opacity: cases[2].opacity, y: cases[2].y }}
              className="group w-full max-w-[calc(50%-1rem)]"
            >
              <div className="bg-gradient-to-br from-[#0f1419] to-[#0a0e13] border border-white/10 rounded-2xl p-8 h-full relative overflow-hidden hover:border-accent-blue/40 transition-all duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-accent-blue/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-6">
                    <div className="text-4xl">{cases[2].icon}</div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-accent-blue mb-1">{cases[2].metric}</div>
                      <div className="text-xs text-gray-500 font-semibold tracking-wider uppercase">{cases[2].metricLabel}</div>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-4">{cases[2].company}</h3>

                  <div className="space-y-4">
                    <div>
                      <div className="text-xs text-red-400 font-semibold tracking-wider uppercase mb-2">Problem</div>
                      <p className="text-sm text-gray-400 leading-relaxed">{cases[2].problem}</p>
                    </div>

                    <div>
                      <div className="text-xs text-accent-blue font-semibold tracking-wider uppercase mb-2">Solution</div>
                      <p className="text-sm text-gray-300 leading-relaxed">{cases[2].solution}</p>
                    </div>

                    <div>
                      <div className="text-xs text-green-400 font-semibold tracking-wider uppercase mb-2">Result</div>
                      <p className="text-sm text-white font-medium leading-relaxed">{cases[2].result}</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* MOBILE VERSION - Static stacked cards, instant load */}
        <div className="lg:hidden space-y-6">
          {cases.map((caseStudy, index) => (
            <div
              key={caseStudy.company}
              className="opacity-0 animate-[fadeIn_0.4s_ease-out_forwards]"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <div className="bg-gradient-to-br from-[#0f1419] to-[#0a0e13] border border-white/10 rounded-xl p-6 relative overflow-hidden active:border-accent-blue/40 transition-colors duration-200">
                <div className="absolute inset-0 bg-gradient-to-br from-accent-blue/5 to-transparent opacity-50" />
                
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-5">
                    <div className="text-3xl">{caseStudy.icon}</div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-accent-blue mb-0.5">{caseStudy.metric}</div>
                      <div className="text-[10px] text-gray-500 font-semibold tracking-wider uppercase">{caseStudy.metricLabel}</div>
                    </div>
                  </div>

                  <h3 className="text-lg font-bold text-white mb-4">{caseStudy.company}</h3>

                  <div className="space-y-3.5">
                    <div>
                      <div className="text-[10px] text-red-400 font-semibold tracking-wider uppercase mb-1.5">Problem</div>
                      <p className="text-[13px] text-gray-400 leading-relaxed">{caseStudy.problem}</p>
                    </div>

                    <div>
                      <div className="text-[10px] text-accent-blue font-semibold tracking-wider uppercase mb-1.5">Solution</div>
                      <p className="text-[13px] text-gray-300 leading-relaxed">{caseStudy.solution}</p>
                    </div>

                    <div>
                      <div className="text-[10px] text-green-400 font-semibold tracking-wider uppercase mb-1.5">Result</div>
                      <p className="text-[13px] text-white font-medium leading-relaxed">{caseStudy.result}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;